import React from 'react';
import { Link } from 'react-router-dom';
import DiscoverySection from '../explore/DiscoverySection';
import OfficialCitation from '../explore/OfficialCitation';
import '../../styles/explore.scss';

function SectionTable({ table }) {
	if (!table) return null;
	return (
		<div className="journey-blueprint__table-wrap">
			<table className="journey-blueprint__table">
				<thead>
					<tr>
						{table.headers.map((h) => <th key={h}>{h}</th>)}
					</tr>
				</thead>
				<tbody>
					{table.rows.map((row) => (
						<tr key={row.join('-')}>
							{row.map((cell, i) => <td key={i}>{cell}</td>)}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function JourneyStepSection({ step, stepIndex, totalSteps, nextStepId }) {
	return (
		<section id={step.id} className="journey-blueprint__step">
			<DiscoverySection
				eyebrow={`Step ${stepIndex + 1} of ${totalSteps}`}
				title={`${step.icon} ${step.name}`}
				intro={step.question}
			>
				{step.lead && <p className="journey-blueprint__lead">{step.lead}</p>}

				{step.sections && step.sections.map((section) => (
					<article key={section.id} className="journey-blueprint__block">
						<h3 className="journey-blueprint__block-title">{section.question}</h3>
						<p className="journey-blueprint__block-answer">{section.answer}</p>
						{section.source && (
							<OfficialCitation
								sourceName={section.source.name}
								sourceUrl={section.source.url}
								lastVerified={section.lastVerified}
								subjectToChange
							/>
						)}
						<SectionTable table={section.table} />
						{section.tips && section.tips.length > 0 && (
							<ul className="journey-blueprint__tips">
								{section.tips.map((tip) => <li key={tip}>{tip}</li>)}
							</ul>
						)}
					</article>
				))}

				{step.related && step.related.length > 0 && (
					<div className="journey-blueprint__related">
						<strong>Go deeper:</strong>
						{step.related.map((r) => (
							<Link key={r.to} to={r.to} className="journey-blueprint__related-link">{r.label}</Link>
						))}
					</div>
				)}

				{step.nextLabel && nextStepId && (
					<a href={`#${nextStepId}`} className="journey-blueprint__next-hint">
						{step.nextLabel}
					</a>
				)}
			</DiscoverySection>
		</section>
	);
}

const PRIMARY_STATS = [
	{ key: 'distance', label: 'Distance', icon: 'fas fa-route' },
	{ key: 'time', label: 'Time', icon: 'fas fa-clock' },
	{ key: 'altitude', label: 'Max alt.', icon: 'fas fa-mountain' },
	{ key: 'sleep', label: 'Sleep', icon: 'fas fa-moon' },
];

const TRAIL_INFO = [
	{ key: 'food', label: 'Food', icon: 'fas fa-utensils' },
	{ key: 'medical', label: 'Medical', icon: 'fas fa-medkit' },
	{ key: 'atm', label: 'ATM', icon: 'fas fa-money-bill-wave' },
	{ key: 'signal', label: 'Signal', icon: 'fas fa-signal' },
	{ key: 'electricity', label: 'Power', icon: 'fas fa-bolt' },
	{ key: 'water', label: 'Water', icon: 'fas fa-tint' },
];

export function TrekDaysSection({ trekDays }) {
	if (!trekDays || trekDays.length === 0) return null;
	return (
		<div className="trek-days">
			<div className="trek-days__meta" aria-hidden="true">
				<span className="trek-days__count">{trekDays.length} days on trail</span>
				<span className="trek-days__hint">Scroll for more →</span>
			</div>
			<div className="trek-days__track">
				{trekDays.map((d) => (
					<article key={d.day} className="trek-day-card">
						<header className="trek-day-card__head">
							<div className="trek-day-card__badge" aria-hidden="true">
								<span className="trek-day-card__badge-num">{d.day}</span>
								<span className="trek-day-card__badge-label">Day</span>
							</div>
							<h4 className="trek-day-card__title">{d.title}</h4>
						</header>

						<dl className="trek-day-card__stats">
							{PRIMARY_STATS.filter(({ key }) => d[key]).map(({ key, label, icon }) => (
								<div key={key} className="trek-day-card__stat">
									<dt><i className={icon} aria-hidden="true" /> {label}</dt>
									<dd>{d[key]}</dd>
								</div>
							))}
						</dl>

						<ul className="trek-day-card__info">
							{TRAIL_INFO.filter(({ key }) => d[key]).map(({ key, label, icon }) => (
								<li key={key} className="trek-day-card__info-item">
									<i className={icon} aria-hidden="true" />
									<span className="trek-day-card__info-label">{label}</span>
									<span className="trek-day-card__info-value">{d[key]}</span>
								</li>
							))}
						</ul>

						{d.teahouses && (
							<p className="trek-day-card__extra">
								<strong>Lodges</strong>
								<span>{d.teahouses}</span>
							</p>
						)}
						{d.highlights && (
							<p className="trek-day-card__highlights">
								<i className="fas fa-star" aria-hidden="true" />
								<span>{d.highlights}</span>
							</p>
						)}
						{d.notes && <p className="trek-day-card__notes">{d.notes}</p>}
					</article>
				))}
			</div>
		</div>
	);
}

export default JourneyStepSection;
